/**
 * DMF type labels — shared between DMFPanel.tsx (modal) and the flat grid's
 * dmf_type array column (issue #585 Task 10.1), so both surfaces reuse one
 * map instead of duplicating it.
 *
 * Full I-V set (2026-09-23 follow-up feedback) — LGM's own linking/matching
 * only cares about II/IV (docs/features/fda-usdmf-integration.md's
 * MATCHABLE_DMF_TYPES, `api/sql/dmf.js:29`), but FDA Source Search's raw
 * snapshot browsing (unlike linking) is not filtered to those two, so a
 * Type I/III/V record can genuinely surface there with no expansion
 * otherwise. Definitions cross-checked against 21 CFR § 314.420 and FDA
 * guidance — same sourcing already recorded in
 * docs/features/fda-usdmf-integration.md's own "TYPE definitions" table.
 * https://www.law.cornell.edu/cfr/text/21/314.420
 * https://www.fda.gov/regulatory-information/search-fda-guidance-documents/drug-master-files-guidelines
 */
export const DMF_TYPE_LABELS: Record<string, string> = {
  I: 'Facility & Personnel',
  II: 'API',
  III: 'Packaging Material',
  IV: 'Excipient',
  V: 'FDA Reference Info',
};

/**
 * DMFPanel's own modal rendering — `"[code] - [label]"` (e.g. `"II - API"`),
 * matching the standard code-expansion pattern established for TE Code
 * (teCodeLabels.ts) and Application Type (applTypeLabels.ts) — 2026-09-23
 * follow-up feedback: this used to drop the raw code entirely and show only
 * the label ('API'), inconsistent with those two.
 */
export function formatDmfType(dmfType: string | null): string {
  if (!dmfType) return '—';
  const label = DMF_TYPE_LABELS[dmfType];
  return label ? `${dmfType} - ${label}` : dmfType;
}

/**
 * dmf.DMF_Products.status is 'A' / 'I' (docs/features/fda-usdmf-integration.md
 * line 70) — expand to `"[code] - [label]"` (e.g. `"A - Active"`) everywhere
 * it's shown to a user, matching the standard code-expansion pattern
 * established for TE Code/Application Type (2026-09-23 follow-up feedback —
 * this used to drop the raw code and show only the word). Shared by
 * DMFPanel.tsx (linked-record panel) and externalLinkRecordAdapter.ts (Link
 * to Source modal's search/review list).
 */
export function formatDmfStatus(status: string | null): string {
  if (status === 'A') return 'A - Active';
  if (status === 'I') return 'I - Inactive';
  return status ?? '—';
}
